import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  TreeSelect,
  Typography,
  message,
} from "antd";
import {
  GiftOutlined,
  MinusCircleOutlined,
  HistoryOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  UserOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { debounce } from "lodash";
import dayjs from "dayjs";
import {
  getListCustomer,
  givePointCustomer,
} from "../../../../api/customer/customer";
import { deductPointCustomer, getPointHistory } from "../../../../api/point/point";
import { errorMessage } from "../../../../lib/apiError";
import useStoreData from "../../../../hooks/useStoreData";
import { transformData } from "../../../../utils/recursive";
import CustomerOption from "../../../../components/CustomOption/CustomerOption";
import usePaginationSelect from "../../../../components/CustomHooks/usePaginationSelect";
import BrandedPageLayout from "../../../../components/Dashboard/BrandedPageLayout";
import { neutral, brand, semantic, spacing, radius, typography, border } from "../../../../theme/tokens";
import { getDisplayNameFromUser } from "../../../../utils/userDisplayName";
import { useSession } from "../../../../auth/session";
import StatusTag from "../../../../components/cms/StatusTag";

const { Option } = Select;
const { Text } = Typography;

const sectionStyle = {
  background: neutral[0],
  border: border.hairline,
  borderRadius: radius.md,
  padding: spacing[6],
};

const sectionTitle = {
  fontSize: typography.size.md,
  fontWeight: typography.weight.semibold,
  color: neutral[900],
  marginBottom: spacing[5],
  letterSpacing: "-0.005em",
};

function PointForm({ mode, onSuccess }) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const { data: allListStore, loading: isLoadStoreAll } = useStoreData();
  // Hook tự giữ danh sách (trước đây nó chỉ nuôi slice `customer`, còn màn này đọc
  // slice — nên hai màn cùng mở sẽ ghi đè dữ liệu của nhau).
  const {
    items: customerItems,
    loading: loadingMoreCustomer,
    handlePopupScroll,
    search: searchCustomer,
  } = usePaginationSelect(getListCustomer);
  const { user } = useSession();

  const storeOptions = useMemo(() => {
    const data = transformData(allListStore?.data);
    return data?.filter((item) => item.parent_id === 0);
  }, [allListStore]);

  const customerOptions = customerItems?.map((item) => ({
    value: item.id,
    avatar: item.avatar,
    phoneNumber: item.phone,
    email: item.email,
    name: item.name,
    customer_code: item.customer_code,
    referral_code: item.referral_code,
    customer_type: item.customer_type,
    is_npp: item.is_npp,
    label: <CustomerOption {...item} />,
  }));

  const effectiveBranchId = useMemo(() => {
    if (user?.data?.type === "SHOP") return form.getFieldValue("branch_id");
    return storeOptions?.[0]?.value;
  }, [user, storeOptions]);

  // Bỏ `getListCustomerAll()` chạy lúc mở màn: nó tải TOÀN BỘ khách hàng (không
  // phân trang) chỉ để nuôi một nhánh state không màn nào đọc. Dropdown đã lấy dữ
  // liệu qua `usePaginationSelect` (20 bản ghi + cuộn tải thêm).

  const handleSearch = useMemo(
    () => debounce((value) => searchCustomer(value?.trim() || ""), 500),
    [searchCustomer]
  );
  useEffect(() => () => handleSearch.cancel(), [handleSearch]);

  const isAdd = mode === "add";

  const onFinish = async (values) => {
    const branchId = user?.data?.type === "SHOP" ? values.branch_id : storeOptions?.[0]?.value;
    if (!branchId) return message.warning("Vui lòng chọn đơn vị");

    const payload = {
      branch_id: branchId,
      customer_id: values.customer_id,
      point: values.point,
      description: values.description || (isAdd ? "Cộng điểm thủ công" : "Trừ điểm thủ công"),
    };

    if (isAdd) {
      if (values.is_expiry_enabled) {
        payload.is_expiry_enabled = true;
        payload.expires_in_days = values.expires_in_days;
      }
    } else {
      payload.deduct_ranking_point = values.deduct_ranking_point || false;
    }

    setIsLoading(true);
    try {
      if (isAdd) {
        const res = await givePointCustomer(payload);
        // BE trả HTTP 200 kèm `data.original` khi từ chối theo nghiệp vụ.
        if (res?.data?.original) {
          message.error(res.data.original.message || "Cộng điểm thất bại");
        } else {
          message.success(res?.data?.message || "Cộng điểm thành công");
          form.resetFields();
          onSuccess?.();
        }
      } else {
        const res = await deductPointCustomer(payload);
        const data = res?.data;
        if (data?.actual_deducted !== undefined) {
          message.success(
            `Trừ điểm thành công: -${data.actual_deducted.toLocaleString()} điểm (còn lại: ${data.remaining_point.toLocaleString()})`
          );
        } else {
          message.success(res?.message || "Trừ điểm thành công");
        }
        form.resetFields();
        onSuccess?.();
      }
    } catch (e) {
      // Lấy đúng câu BE trả (vượt hạn mức, khách bị khóa, sai đơn vị...) thay vì
      // luôn hiện "Lỗi hệ thống".
      message.error(errorMessage(e, "Lỗi hệ thống, vui lòng thử lại"));
    } finally {
      setIsLoading(false);
    }
  };

  const selectedCustomer = customerOptions?.find(
    (c) => c.value === form.getFieldValue("customer_id")
  );

  return (
    <Form layout="vertical" form={form} requiredMark={false} onFinish={onFinish}>
      <Row gutter={[spacing[6], spacing[6]]}>
        <Col xl={15} lg={15} md={24} sm={24} xs={24}>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[5] }}>
            {/* Khách hàng & Đơn vị */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>Khách hàng & Đơn vị</div>
              <Row gutter={[spacing[4], spacing[4]]}>
                {user?.data?.type === "SHOP" && (
                  <Col span={24}>
                    <Form.Item
                      label="Đơn vị"
                      name="branch_id"
                      required
                      rules={[{ required: true, message: "Vui lòng chọn đơn vị" }]}
                      style={{ marginBottom: 0 }}
                    >
                      <TreeSelect
                        loading={isLoadStoreAll}
                        treeData={storeOptions}
                        placeholder="Chọn đơn vị"
                        allowClear
                        showSearch
                        filterTreeNode={(input, node) =>
                          node.title.toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                )}
                <Col span={24}>
                  <Form.Item
                    label="Khách hàng"
                    name="customer_id"
                    required
                    rules={[{ required: true, message: "Vui lòng chọn khách hàng" }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      loading={loadingMoreCustomer}
                      allowClear
                      showSearch
                      virtual
                      placeholder="Tìm theo SĐT, tên hoặc mã KH"
                      filterOption={false}
                      onSearch={handleSearch}
                      onPopupScroll={handlePopupScroll}
                      optionLabelProp="name"
                    >
                      {customerOptions?.map((item) => (
                        <Option key={item.value} value={item.value} name={item.name}>
                          {item.label}
                        </Option>
                      ))}
                      {loadingMoreCustomer && (
                        <Option key="__loading" disabled>
                          <Skeleton avatar={{ size: 40 }} active paragraph={{ rows: 1 }} />
                        </Option>
                      )}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Số điểm */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>
                {isAdd ? "Thông tin điểm thưởng" : "Thông tin trừ điểm"}
              </div>
              <Row gutter={[spacing[4], spacing[4]]}>
                <Col span={24}>
                  <Form.Item
                    label="Số điểm"
                    name="point"
                    required
                    rules={[{ required: true, message: "Vui lòng nhập số điểm" }]}
                    style={{ marginBottom: spacing[4] }}
                  >
                    <InputNumber
                      min={1}
                      placeholder={isAdd ? "Nhập số điểm muốn cộng" : "Nhập số điểm muốn trừ"}
                      style={{ width: "100%" }}
                      formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      parser={(v) => v.replace(/\$\s?|(,*)/g, "")}
                    />
                  </Form.Item>
                </Col>

                {isAdd && (
                  <>
                    <Col span={24}>
                      <Form.Item
                        label="Áp dụng hạn sử dụng"
                        name="is_expiry_enabled"
                        valuePropName="checked"
                        style={{ marginBottom: spacing[3] }}
                      >
                        <Switch
                          checkedChildren="Có hạn dùng"
                          unCheckedChildren="Mặc định / Vĩnh viễn"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, cur) => prev.is_expiry_enabled !== cur.is_expiry_enabled}
                      >
                        {({ getFieldValue }) =>
                          getFieldValue("is_expiry_enabled") ? (
                            <Form.Item
                              label="Số ngày hết hạn"
                              name="expires_in_days"
                              rules={[{ required: true, message: "Vui lòng nhập số ngày" }]}
                              style={{ marginBottom: 0 }}
                            >
                              <InputNumber
                                min={1}
                                placeholder="Ví dụ: 30, 90, 365..."
                                style={{ width: "100%" }}
                                addonAfter="ngày"
                              />
                            </Form.Item>
                          ) : null
                        }
                      </Form.Item>
                    </Col>
                  </>
                )}

                {!isAdd && (
                  <Col span={24}>
                    <Form.Item
                      label="Đồng thời trừ điểm xếp hạng"
                      name="deduct_ranking_point"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch
                        checkedChildren="Có"
                        unCheckedChildren="Không"
                      />
                    </Form.Item>
                    <Text style={{ fontSize: typography.size.xs, color: neutral[400], marginTop: spacing[1], display: "block" }}>
                      Nếu bật, tổng điểm xếp hạng cũng sẽ bị trừ theo
                    </Text>
                  </Col>
                )}
              </Row>
            </div>

            {/* Lý do */}
            <div style={sectionStyle}>
              <Form.Item
                label={isAdd ? "Lý do cộng điểm" : "Lý do trừ điểm"}
                name="description"
                rules={[{ required: true, message: "Vui lòng nhập lý do" }]}
                style={{ marginBottom: 0 }}
              >
                <Input.TextArea
                  rows={3}
                  placeholder={
                    isAdd
                      ? "Ví dụ: Tặng điểm chào mừng, quà sinh nhật..."
                      : "Ví dụ: Điều chỉnh sai số, vi phạm chính sách..."
                  }
                />
              </Form.Item>
            </div>
          </div>
        </Col>

        {/* Sidebar */}
        <Col xl={9} lg={9} md={24} sm={24} xs={24}>
          <div style={{ position: "sticky", top: spacing[6] }}>
            <div
              style={{
                background: neutral[0],
                border: border.hairline,
                borderRadius: radius.md,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: `${spacing[4]}px ${spacing[6]}px`,
                  background: isAdd ? semantic.successSubtle : semantic.dangerSubtle,
                  borderBottom: border.divider,
                }}
              >
                <Text
                  style={{
                    fontSize: typography.size.md,
                    fontWeight: typography.weight.semibold,
                    color: isAdd ? semantic.success : semantic.danger,
                  }}
                >
                  {isAdd ? (
                    <><ArrowUpOutlined style={{ marginRight: 8 }} />Cộng điểm</>
                  ) : (
                    <><ArrowDownOutlined style={{ marginRight: 8 }} />Trừ điểm</>
                  )}
                </Text>
              </div>

              <div style={{ padding: spacing[6] }}>
                <Alert
                  type={isAdd ? "info" : "warning"}
                  showIcon
                  message={
                    isAdd
                      ? "Điểm sẽ được cộng trực tiếp vào tài khoản khách hàng và ghi nhận lịch sử."
                      : "Điểm sẽ bị trừ theo cơ chế FIFO (ưu tiên lô gần hết hạn trước). Không thể trừ âm."
                  }
                  style={{ marginBottom: spacing[5] }}
                />

                <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
                  {() => {
                    const point = form.getFieldValue("point");
                    return (
                      <div style={{ textAlign: "center", padding: `${spacing[3]}px 0` }}>
                        <Text style={{ fontSize: typography.size.xs, color: neutral[500], textTransform: "uppercase", letterSpacing: "0.04em" }}>
                          Số điểm thao tác
                        </Text>
                        <div
                          style={{
                            fontSize: typography.size["3xl"],
                            fontWeight: typography.weight.bold,
                            color: isAdd
                              ? (point > 0 ? semantic.success : neutral[900])
                              : (point > 0 ? semantic.danger : neutral[900]),
                            marginTop: spacing[2],
                            lineHeight: 1.1,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {point > 0 ? `${isAdd ? "+" : "-"}${Number(point).toLocaleString()}` : "0"}
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>
              </div>

              <div style={{ padding: `0 ${spacing[6]}px ${spacing[6]}px`, display: "flex", flexDirection: "column", gap: spacing[3] }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={isLoading}
                  danger={!isAdd}
                  block
                >
                  {isAdd ? "Xác nhận cộng điểm" : "Xác nhận trừ điểm"}
                </Button>
                <Button
                  block
                  size="large"
                  icon={<ReloadOutlined />}
                  onClick={() => form.resetFields()}
                >
                  Làm mới
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Form>
  );
}

function PointHistoryTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filter, setFilter] = useState(null);

  const fetchData = async (page = 1, size = 10, type = null) => {
    setLoading(true);
    try {
      const res = await getPointHistory({
        currentPage: page,
        pageSize: size,
        fluctuation_type: type,
      });
      const paginated = res?.data;
      setData(paginated?.data || []);
      setPagination({
        current: paginated?.current_page || page,
        pageSize: paginated?.per_page || size,
        total: paginated?.total || 0,
      });
    } catch (e) {
      message.error(errorMessage(e, "Không tải được lịch sử"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, 10, filter);
  }, [filter]);

  const columns = [
    {
      title: "#",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => (pagination.current - 1) * pagination.pageSize + i + 1,
    },
    {
      title: "Khách hàng",
      key: "customer",
      width: 180,
      ellipsis: true,
      render: (_, r) => (
        <Space>
          <UserOutlined />
          <Text>{r.customer_t?.name || "—"}</Text>
        </Space>
      ),
    },
    {
      title: "Chi nhánh",
      key: "branch",
      width: 160,
      ellipsis: true,
      render: (_, r) => (
        <Space>
          <ShopOutlined />
          <Text>{r.branch_t?.name || "—"}</Text>
        </Space>
      ),
    },
    {
      title: "Loại",
      dataIndex: "fluctuation_type",
      key: "fluctuation_type",
      align: "center",
      width: 100,
      render: (type) =>
        type === "INCREASE" ? (
          <StatusTag tone="success"><ArrowUpOutlined /> Cộng</StatusTag>
        ) : (
          <StatusTag tone="danger"><ArrowDownOutlined /> Trừ</StatusTag>
        ),
    },
    {
      title: "Điểm",
      dataIndex: "point",
      key: "point",
      align: "right",
      width: 120,
      render: (val, r) => (
        <Text
          style={{
            fontWeight: typography.weight.semibold,
            color: r.fluctuation_type === "INCREASE" ? semantic.success : semantic.danger,
          }}
        >
          {r.fluctuation_type === "INCREASE" ? "+" : "-"}{Number(val).toLocaleString()}
        </Text>
      ),
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      width: 220,
      ellipsis: true,
    },
    {
      title: "Người thực hiện",
      key: "user",
      width: 160,
      render: (_, r) => {
        return getDisplayNameFromUser(r.user, "—");
      },
    },
    {
      title: "Thời gian",
      dataIndex: "created_at",
      key: "created_at",
      width: 170,
      render: (val) => (
        <Space>
          <HistoryOutlined />
          <Text>{val ? dayjs(val).format("DD-MM-YYYY HH:mm") : "—"}</Text>
        </Space>
      ),
    },
  ];

  return (
    <div style={sectionStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: spacing[5] }}>
        <div style={sectionTitle}>Lịch sử cộng/trừ điểm</div>
        <Select
          value={filter}
          onChange={setFilter}
          style={{ width: 160 }}
          allowClear
          placeholder="Tất cả"
        >
          <Option value="INCREASE">Chỉ cộng điểm</Option>
          <Option value="DECREASE">Chỉ trừ điểm</Option>
        </Select>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        size="middle"
        scroll={{ x: "max-content" }}
        pagination={{
          ...pagination,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50],
          onChange: (page, size) => fetchData(page, size, filter),
        }}
        locale={{ emptyText: <Empty description="Chưa có lịch sử" /> }}
      />
    </div>
  );
}

export default function SendPointCustomer() {
  const [mode, setMode] = useState("ADD");
  const [historyKey, setHistoryKey] = useState(0);

  const handleSuccess = () => {
    setHistoryKey((k) => k + 1);
  };

  return (
    <BrandedPageLayout
      title="Quản lý điểm"
      subtitle="Cộng hoặc trừ điểm thủ công cho khách hàng. Mọi thao tác đều được ghi nhận lịch sử đầy đủ."
      breadcrumbItems={[{ title: "Khách hàng" }, { title: "Quản lý điểm" }]}
    >
      <div
        style={{
          background: neutral[0],
          borderRadius: radius.md,
          border: border.hairline,
          padding: `${spacing[2]}px ${spacing[6]}px ${spacing[6]}px`,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: spacing[2],
            marginBottom: spacing[5],
            borderBottom: border.divider,
            paddingBottom: spacing[3],
          }}
        >
          {[
            { key: "ADD", icon: <GiftOutlined />, label: "Cộng điểm" },
            { key: "DEDUCT", icon: <MinusCircleOutlined />, label: "Trừ điểm" },
            { key: "HISTORY", icon: <HistoryOutlined />, label: "Lịch sử" },
          ].map((tab) => (
            <Button
              key={tab.key}
              type={mode === tab.key ? "primary" : "text"}
              ghost={mode === tab.key}
              icon={tab.icon}
              onClick={() => setMode(tab.key)}
              style={{
                fontWeight: mode === tab.key ? typography.weight.semibold : typography.weight.regular,
                borderRadius: radius.base,
              }}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {mode === "ADD" && <PointForm mode="add" onSuccess={handleSuccess} />}
        {mode === "DEDUCT" && <PointForm mode="deduct" onSuccess={handleSuccess} />}
        {mode === "HISTORY" && <PointHistoryTab key={historyKey} />}
      </div>
    </BrandedPageLayout>
  );
}
